import type {ButtonColor, ButtonProps, ButtonSize, ButtonVariant} from '../button/Button';
import type {ReactNode} from 'react';

import {cn} from '../../helpers/cn';
import {Button} from '../button/Button';

export type IconButtonProps = Omit<
  ButtonProps,
  'startIcon' | 'endIcon' | 'fullWidth' | 'children'
> & {
  /** Icon content — keep decorative icons `aria-hidden` on the child node. */
  children: ReactNode;
  'aria-label': string;
  /** Keeps background and text color unchanged on hover/active (e.g. modal close, nav search). */
  disableHover?: boolean;
};

export type {ButtonColor, ButtonSize, ButtonVariant};

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'size-8 shrink-0 p-0',
  md: 'size-10 shrink-0 p-0',
  lg: 'size-11 shrink-0 p-0'
};

/** Icon glyph size is owned by IconButton — do not size icons via className on children. */
const iconSizeClasses: Record<ButtonSize, string> = {
  sm: '[&_.anticon]:text-lg',
  md: '[&_.anticon]:text-lg',
  lg: '[&_.anticon]:text-2xl'
};

const disableHoverClasses =
  'hover:bg-transparent! active:bg-transparent! hover:text-neutral-fg! active:text-neutral-fg! focus:outline-none focus-visible:outline-none';

export function IconButton({
  variant = 'ghost',
  color = 'secondary',
  size = 'lg',
  className,
  disableHover = false,
  children,
  ...props
}: IconButtonProps) {
  return (
    <Button
      variant={variant}
      color={color}
      size={size}
      disableWave={disableHover}
      className={cn(sizeClasses[size], disableHover && disableHoverClasses, className)}
      {...props}
    >
      <span
        className={cn(
          'inline-flex items-center justify-center leading-none [&_.anticon]:leading-none',
          iconSizeClasses[size]
        )}
      >
        {children}
      </span>
    </Button>
  );
}
